import {
  logEvent,
  setUserId as setFirebaseUserId,
  setUserProperties as setFirebaseUserProperties,
} from 'firebase/analytics';

import { firebaseAnalytics } from './firebase';

export const trackPageView = (url: string, title?: string) => {
  if (firebaseAnalytics) {
    logEvent(firebaseAnalytics, 'page_view', {
      page_title: title || document.title,
      page_location: url,
    });
  }
};

export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (firebaseAnalytics) {
    logEvent(firebaseAnalytics, action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

export const trackUserEngagement = (engagementTimeMs: number) => {
  if (firebaseAnalytics) {
    logEvent(firebaseAnalytics, 'user_engagement', {
      engagement_time_msec: engagementTimeMs,
    });
  }
};

export const setUserProperties = (properties: Record<string, unknown>) => {
  if (firebaseAnalytics) {
    setFirebaseUserProperties(firebaseAnalytics, properties);
  }
};

export const setUserId = (userId: string) => {
  if (firebaseAnalytics) {
    setFirebaseUserId(firebaseAnalytics, userId);
  }
};

export const trackPurchase = (
  transactionId: string,
  value: number,
  currency: string = 'USD',
  items?: Array<{
    item_id: string;
    item_name: string;
    price: number;
    quantity: number;
  }>
) => {
  if (firebaseAnalytics) {
    logEvent(firebaseAnalytics, 'purchase', {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      items: items,
    });
  }
};

export const trackChallengeEvent = (
  action: 'view' | 'join' | 'trade' | 'complete',
  challengeId: string,
  challengeName?: string,
  value?: number
) => {
  trackEvent(action, 'challenge', challengeName || challengeId, value);
};

export const trackTradingEvent = (
  action: 'buy' | 'sell' | 'hold',
  coinId: string,
  coinName?: string,
  amount?: number
) => {
  trackEvent(action, 'trading', coinName || coinId, amount);
};

export const trackNavigation = (destination: string) => {
  trackEvent('navigate', 'navigation', destination);
};

export const trackError = (error: string, errorCode?: string) => {
  trackEvent(
    'error',
    'error',
    error,
    errorCode ? parseInt(errorCode) : undefined
  );
};

export const trackChallengeParticipant = (
  action: 'join' | 'leave' | 'active' | 'inactive',
  challengeId: string,
  challengeName: string,
  participantId: string,
  participantType: 'new' | 'returning' | 'veteran',
  challengeDuration?: number
) => {
  if (firebaseAnalytics) {
    logEvent(firebaseAnalytics, 'challenge_participant', {
      action: action,
      challenge_id: challengeId,
      challenge_name: challengeName,
      participant_id: participantId,
      participant_type: participantType,
      challenge_duration: challengeDuration,
      timestamp: Date.now(),
    });
  }
};

export const trackCoinTransaction = (
  action: 'buy' | 'sell',
  coinId: string,
  coinName: string,
  coinSymbol: string,
  amount: number,
  price: number,
  totalValue: number,
  currency: string = 'USD',
  challengeId?: string,
  challengeName?: string
) => {
  if (firebaseAnalytics) {
    logEvent(firebaseAnalytics, 'coin_transaction', {
      action: action,
      coin_id: coinId,
      coin_name: coinName,
      coin_symbol: coinSymbol,
      amount: amount,
      price: price,
      total_value: totalValue,
      currency: currency,
      challenge_id: challengeId,
      challenge_name: challengeName,
      timestamp: Date.now(),
    });
  }
};

export const trackChallengePerformance = (
  challengeId: string,
  challengeName: string,
  totalParticipants: number,
  activeParticipants: number,
  totalVolume: number,
  averageProfit: number,
  completionRate: number,
  challengeStatus: 'active' | 'completed' | 'cancelled'
) => {
  if (firebaseAnalytics) {
    logEvent(firebaseAnalytics, 'challenge_performance', {
      challenge_id: challengeId,
      challenge_name: challengeName,
      total_participants: totalParticipants,
      active_participants: activeParticipants,
      total_volume: totalVolume,
      average_profit: averageProfit,
      completion_rate: completionRate,
      challenge_status: challengeStatus,
      timestamp: Date.now(),
    });
  }
};

export const trackPortfolioChange = (
  userId: string,
  totalValue: number,
  previousValue: number,
  changePercentage: number,
  topHoldings: Array<{
    coinId: string;
    coinName: string;
    value: number;
    percentage: number;
  }>,
  currency: string = 'USD'
) => {
  if (firebaseAnalytics) {
    logEvent(firebaseAnalytics, 'portfolio_change', {
      user_id: userId,
      total_value: totalValue,
      previous_value: previousValue,
      change_percentage: changePercentage,
      top_holdings: topHoldings,
      currency: currency,
      timestamp: Date.now(),
    });
  }
};

export const trackPopularCoin = (
  coinId: string,
  coinName: string,
  coinSymbol: string,
  totalTransactions: number,
  totalVolume: number,
  averagePrice: number,
  priceChange24h: number,
  marketCap?: number,
  rank?: number
) => {
  if (firebaseAnalytics) {
    logEvent(firebaseAnalytics, 'popular_coin', {
      coin_id: coinId,
      coin_name: coinName,
      coin_symbol: coinSymbol,
      total_transactions: totalTransactions,
      total_volume: totalVolume,
      average_price: averagePrice,
      price_change_24h: priceChange24h,
      market_cap: marketCap,
      rank: rank,
      timestamp: Date.now(),
    });
  }
};

export const trackUserSession = (
  userId: string,
  sessionDuration: number,
  pagesVisited: number,
  challengesViewed: number,
  tradesMade: number,
  sessionType: 'active' | 'passive' | 'trading_focused'
) => {
  if (firebaseAnalytics) {
    logEvent(firebaseAnalytics, 'user_session', {
      user_id: userId,
      session_duration: sessionDuration,
      pages_visited: pagesVisited,
      challenges_viewed: challengesViewed,
      trades_made: tradesMade,
      session_type: sessionType,
      timestamp: Date.now(),
    });
  }
};

export const trackLeaderboardInteraction = (
  action: 'view' | 'filter' | 'sort',
  leaderboardType: 'global' | 'challenge' | 'friends',
  filterCriteria?: string,
  sortBy?: string,
  challengeId?: string
) => {
  if (firebaseAnalytics) {
    logEvent(firebaseAnalytics, 'leaderboard_interaction', {
      action: action,
      leaderboard_type: leaderboardType,
      filter_criteria: filterCriteria,
      sort_by: sortBy,
      challenge_id: challengeId,
      timestamp: Date.now(),
    });
  }
};

export const trackTaskEvent = (
  action: 'view' | 'start' | 'complete' | 'claim',
  taskId: string,
  taskTitle: string,
  taskCategory: 'social' | 'daily' | 'partners' | 'tasks' | 'achievements',
  reward?: number,
  userId?: string
) => {
  if (firebaseAnalytics) {
    logEvent(firebaseAnalytics, 'task_event', {
      action: action,
      task_id: taskId,
      task_title: taskTitle,
      task_category: taskCategory,
      reward: reward,
      user_id: userId,
      timestamp: Date.now(),
    });
  }
};
